import { PureComponent, type ReactElement } from "react";
import { connect } from "react-redux";
import {
  FieldArray,
  type FieldArrayFieldsProps,
  type InjectedFormProps,
  reduxForm,
  type WrappedFieldArrayProps,
} from "redux-form";
import { CalloutIntent } from "widgets";

import { selMessage } from "@/messages/message.ts";
import { isDefinedNumber } from "@/redux/modules/sample-admin.ts";

import { isConfigurableParentAttribute } from "../../app-utils/sample.js";
import { filterTemplateSuggestions } from "../../app-utils/sample-template.js";
import { trimOrThrow } from "../../app-utils/string.js";
import { type Attribute, type RawAttributeDefault } from "../../graphql.js";
import { type IAppState } from "../../redux/reducer.js";
import { Dialog, type IDialogMessage } from "../core/overlays/Dialogs.js";
import ParentAttributeConfigurationFields from "../core/redux-form/ParentAttributeConfigurationFields.js";
import { type IAttributeWithDefault } from "./AddSampleTypeDialog.js";

interface IConfigureParentTypeAttributeDialogOwnProps {
  onApply?: (configuredAttributes: IAttributeWithDefault[]) => void;
  onCancel?: () => void;
  templateAttributes: Attribute[];
  attributes: IAttributeWithDefault[];
  parentTypeName: string;
  usedInOtherSampleTypes?: boolean;
}

interface IFieldValue extends Partial<RawAttributeDefault> {
  attribute: IAttributeWithDefault;
}

interface IInitialValues {
  attributes?: IFieldValue[];
}

interface IConfigureParentTypeAttributeDialogAppStateProps {
  initialValues: IInitialValues;
}

interface IConfigureParentTypeAttributeDialogDispatchProps {}

export interface IConfigureParentTypeAttributeDialogProps
  extends IConfigureParentTypeAttributeDialogAppStateProps,
    IConfigureParentTypeAttributeDialogDispatchProps,
    IConfigureParentTypeAttributeDialogOwnProps,
    InjectedFormProps<IInitialValues, any> {}

function mapStateToProps(
  _state: IAppState,
  ownProps: IConfigureParentTypeAttributeDialogOwnProps,
): IConfigureParentTypeAttributeDialogAppStateProps {
  const initialValues: IFieldValue[] =
    ownProps.attributes &&
    ownProps.attributes.filter(isConfigurableParentAttribute).map((attribute: IAttributeWithDefault) => {
      return {
        attribute: attribute,
        [ConfigureParentTypeAttributeDialogImpl.CONFIGURE_PARENT_TYPE_ATTRIBUTE_DEFAULT_NUMBER]:
          attribute.attributeDefault &&
          isDefinedNumber(attribute.attributeDefault.defaultNumber) &&
          String(attribute.attributeDefault.defaultNumber),
        [ConfigureParentTypeAttributeDialogImpl.CONFIGURE_PARENT_TYPE_ATTRIBUTE_DEFAULT_TEXT]:
          attribute.attributeDefault?.defaultText,
        [ConfigureParentTypeAttributeDialogImpl.CONFIGURE_PARENT_TYPE_ATTRIBUTE_DEFAULT_VOC_TERM]:
          attribute.attributeDefault?.defaultTerm,
        [ConfigureParentTypeAttributeDialogImpl.CONFIGURE_PARENT_TYPE_ATTRIBUTE_TEMPLATE]:
          attribute.attributeDefault?.template,
      };
    });

  return {
    initialValues: { attributes: initialValues },
  };
}

function mapDispatchToProps(_dispatch): IConfigureParentTypeAttributeDialogDispatchProps {
  return {};
}

// TODO merge ConfigureParentTypeAttributeDialog & ConfigureSampleTypeAttributeDialog ?
class ConfigureParentTypeAttributeDialogImpl extends PureComponent<IConfigureParentTypeAttributeDialogProps> {
  public static CONFIGURE_PARENT_TYPE_ATTRIBUTE_FORM_NAME = "CONFIGURE_PARENT_TYPE_ATTRIBUTE_FORM";
  public static CONFIGURE_PARENT_TYPE_ATTRIBUTE_ATTRIBUTES_FIELD_ARRAY = "attributes";
  public static CONFIGURE_PARENT_TYPE_ATTRIBUTE_DEFAULT_NUMBER = "defaultNumber";
  public static CONFIGURE_PARENT_TYPE_ATTRIBUTE_DEFAULT_TEXT = "defaultText";
  public static CONFIGURE_PARENT_TYPE_ATTRIBUTE_DEFAULT_VOC_TERM = "defaultVocTerm";
  public static CONFIGURE_PARENT_TYPE_ATTRIBUTE_TEMPLATE = "template";

  constructor(props: IConfigureParentTypeAttributeDialogProps) {
    super(props);
  }

  attributeDefaultExists = (attributeDefault: Partial<RawAttributeDefault>) => {
    return (
      attributeDefault &&
      (attributeDefault.defaultTerm ||
        attributeDefault.defaultText ||
        isDefinedNumber(attributeDefault.defaultNumber) ||
        attributeDefault.template)
    );
  };

  handleSubmitData = (values): Promise<any> => {
    const configuredAttributes: IAttributeWithDefault[] = this.props.attributes.map(
      (attribute: IAttributeWithDefault) => {
        const configuredAttributeIndex: number = values.attributes.findIndex(
          (valueRow: any) => valueRow.attribute.id === attribute.id,
        );
        let configurationPayload: Partial<RawAttributeDefault>;
        if (configuredAttributeIndex > -1) {
          const configuredValueRow: any = values.attributes[configuredAttributeIndex];
          const numberDefault =
            configuredValueRow[ConfigureParentTypeAttributeDialogImpl.CONFIGURE_PARENT_TYPE_ATTRIBUTE_DEFAULT_NUMBER];
          configurationPayload = {
            defaultNumber: !numberDefault ? null : Number(numberDefault),
            defaultText: trimOrThrow(
              configuredValueRow[ConfigureParentTypeAttributeDialogImpl.CONFIGURE_PARENT_TYPE_ATTRIBUTE_DEFAULT_TEXT],
            ),
            defaultTerm:
              configuredValueRow[
                ConfigureParentTypeAttributeDialogImpl.CONFIGURE_PARENT_TYPE_ATTRIBUTE_DEFAULT_VOC_TERM
              ],
            template: trimOrThrow(
              configuredValueRow[ConfigureParentTypeAttributeDialogImpl.CONFIGURE_PARENT_TYPE_ATTRIBUTE_TEMPLATE],
            ),
          };
        }
        return {
          ...attribute,
          attributeDefault: this.attributeDefaultExists(configurationPayload) && configurationPayload,
        };
      },
    );

    this.props.onApply(configuredAttributes);
    return Promise.resolve(true);
  };

  buildParentAttributeConfigurationFieldsComponent = (props: any) => {
    // props should be  WrappedFieldArrayProps<IFieldValue>, but the typescript definition dont like it ...
    const fields: FieldArrayFieldsProps<IFieldValue> = (props as WrappedFieldArrayProps<IFieldValue>).fields;
    return (
      <div>
        {fields.map((element: string, index) => {
          // CAUTION: the "map" call returns a string, since "map" is not our standard "map" but provided by redux-form
          // Therefore we need to retrieve the parent type explicitly via redux-form "get":

          const attribute: Attribute = fields.get(index).attribute;

          if (!isConfigurableParentAttribute(attribute)) {
            return;
          }

          return (
            <ParentAttributeConfigurationFields
              fieldName={element}
              key={`${element}-${index}`}
              attribute={attribute}
              templateAttributes={filterTemplateSuggestions(this.props.templateAttributes, attribute)}
            />
          );
        })}
      </div>
    );
  };

  render(): ReactElement {
    const footerMessage: IDialogMessage = this.props.error
      ? { message: this.props.error, intent: CalloutIntent.Danger }
      : this.props.usedInOtherSampleTypes
        ? { message: selMessage({ id: "admin.sample.parent.template.warnMessage" }), intent: CalloutIntent.Warning }
        : null;

    return (
      <Dialog
        headerProps={{ title: `Configure ${this.props.parentTypeName} Attributes` }}
        mainActionButtonProps={{
          children: `Apply`,
          isDisabled: this.props.submitting || this.props.invalid,
          onPress: this.props.handleSubmit(this.handleSubmitData),
        }}
        footerMessage={footerMessage}
        closeFn={this.props.onCancel}
      >
        <p>You can add some specific attribute configuration when used in the context of this sample type.</p>

        <FieldArray
          name={ConfigureParentTypeAttributeDialogImpl.CONFIGURE_PARENT_TYPE_ATTRIBUTE_ATTRIBUTES_FIELD_ARRAY}
          component={this.buildParentAttributeConfigurationFieldsComponent}
        />
      </Dialog>
    );
  }

  static validate = (allValues: IInitialValues) => {
    const arrayValues =
      allValues[ConfigureParentTypeAttributeDialogImpl.CONFIGURE_PARENT_TYPE_ATTRIBUTE_ATTRIBUTES_FIELD_ARRAY];
    const bothTextAndTemplateFilled =
      arrayValues &&
      !!arrayValues.find((rowValues) => {
        const defaultText: string =
          rowValues[ConfigureParentTypeAttributeDialogImpl.CONFIGURE_PARENT_TYPE_ATTRIBUTE_DEFAULT_TEXT];
        const template: string =
          rowValues[ConfigureParentTypeAttributeDialogImpl.CONFIGURE_PARENT_TYPE_ATTRIBUTE_TEMPLATE];
        return !!defaultText && !!template;
      });

    return bothTextAndTemplateFilled ? { _error: "You can fill only one of default text or template fields." } : {};
  };
}

const ConfigureParentTypeAttributeDialog = connect<
  IConfigureParentTypeAttributeDialogAppStateProps,
  IConfigureParentTypeAttributeDialogDispatchProps,
  IConfigureParentTypeAttributeDialogOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: ConfigureParentTypeAttributeDialogImpl.CONFIGURE_PARENT_TYPE_ATTRIBUTE_FORM_NAME,
    validate: ConfigureParentTypeAttributeDialogImpl.validate,
  })(ConfigureParentTypeAttributeDialogImpl) as any,
);

export { ConfigureParentTypeAttributeDialog, ConfigureParentTypeAttributeDialogImpl };
export default ConfigureParentTypeAttributeDialog;
